import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { TextField, Button, Typography, Box, Alert, Link, Checkbox, FormControlLabel } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import apiClient from '../../utils/axios';
import AuthCard, { authFieldSx } from '../../components/AuthCard';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^01[0-9]{9}$/;

const validateUsername = (username) => {
  if (!username) return '사용자명을 입력하세요.';
  if (username.toLowerCase() === 'admin') return '"admin"은 사용할 수 없는 사용자명입니다.';
  return '';
};

const validatePhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return '휴대폰 번호를 입력하세요.';
  if (!PHONE_RE.test(phoneNumber.replace(/[-\s]/g, ''))) {
    return '올바른 휴대폰 번호를 입력하세요. (예: 01012345678)';
  }
  return '';
};

const openPopup = (path, name) => (e) => {
  e.preventDefault();
  const width = 800;
  const height = 900;
  const left = (window.screen.width - width) / 2;
  const top = (window.screen.height - height) / 2;
  window.open(path, name, `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`);
};

const checkboxSx = {
  color: 'text.primary',
  '&.Mui-checked': { color: 'text.primary' },
};

const viewLinkSx = {
  color: 'text.secondary',
  fontSize: '0.875rem',
  textDecoration: 'none',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: 0.5,
  '&:hover': { textDecoration: 'underline' },
};

const ConsentRow = ({ checked, onChange, label, onView }) => (
  <Box sx={{ mb: 0, ml: 1, display: 'flex', alignItems: 'center', width: '100%' }}>
    <Checkbox checked={checked} onChange={onChange} sx={{ ...checkboxSx, padding: '4px' }} />
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flex: 1, ml: 0.5 }}>
      <Typography sx={{ fontSize: '0.875rem' }}>
        <span style={{ color: '#d32f2f' }}>(필수)</span> {label}
      </Typography>
      {onView && (
        <Link component="button" type="button" onClick={onView} sx={viewLinkSx}>
          보기
          <span style={{ fontSize: '0.75rem' }}>↗</span>
        </Link>
      )}
    </Box>
  </Box>
);

const Register = () => {
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [usernameError, setUsernameError] = useState('');
  const [phoneNumberError, setPhoneNumberError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [loading, setLoading] = useState(false);
  const [agree, setAgree] = useState({ age: false, terms: false, privacy: false });
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnUrl = searchParams.get('returnUrl') || '';

  const agreeAll = agree.age && agree.terms && agree.privacy;

  const checkEmailAvailability = async (value) => {
    if (!value) {
      setEmailError('');
      return true;
    }
    if (!EMAIL_RE.test(value)) {
      setEmailError('유효한 이메일 주소를 입력하세요.');
      return false;
    }
    try {
      setCheckingEmail(true);
      const response = await apiClient.post('/auth/check-email', { email: value });
      if (!response.data.available) {
        setEmailError(response.data.message || '이미 사용 중인 이메일입니다.');
        return false;
      }
      setEmailError('');
      return true;
    } catch (err) {
      setEmailError(err.response?.data?.error || '이메일 중복 체크 중 오류가 발생했습니다.');
      return false;
    } finally {
      setCheckingEmail(false);
    }
  };

  // 입력이 멈춘 뒤에만 중복 체크
  useEffect(() => {
    if (!email || !EMAIL_RE.test(email)) return undefined;
    const t = setTimeout(() => checkEmailAvailability(email), 500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');

    if (!email) return setEmailError('이메일을 입력하세요.');
    if (!EMAIL_RE.test(email)) return setEmailError('유효한 이메일 주소를 입력하세요.');

    const uErr = validateUsername(username);
    if (uErr) return setUsernameError(uErr);
    const pErr = validatePhoneNumber(phoneNumber);
    if (pErr) return setPhoneNumberError(pErr);

    if (!password) return setPasswordError('비밀번호를 입력하세요.');
    if (password.length < 6) return setPasswordError('비밀번호는 최소 6자 이상이어야 합니다.');
    if (!confirmPassword) return setPasswordError('비밀번호를 다시 입력하세요.');
    if (password !== confirmPassword) return setPasswordError('비밀번호가 일치하지 않습니다.');

    if (!agree.age) return setError('만 14세 이상 동의는 필수입니다.');
    if (!agree.terms) return setError('이용약관 동의는 필수입니다.');
    if (!agree.privacy) return setError('개인정보 수집 및 이용 동의는 필수입니다.');

    setLoading(true);
    try {
      if (!(await checkEmailAvailability(email))) return;
      const result = await register(email, username, phoneNumber.replace(/[-\s]/g, ''), password, returnUrl);
      if (result.success) {
        const next = new URLSearchParams({ email: result.email || email });
        if (returnUrl) next.set('returnUrl', returnUrl);
        navigate(`/verify-email?${next.toString()}`);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('오류가 발생했습니다. 다시 시도해주세요.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthCard
      title="회원가입"
      titleMb={3}
      header={
        <Typography sx={{ mb: 2, fontSize: '0.875rem', color: 'text.primary' }}>
          PlanGo Agent 사용을 위해서는 e-mail 인증을 통한 회원가입 이후 사용가능합니다
        </Typography>
      }
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box component="form" onSubmit={handleRegister}>
        <TextField
          fullWidth
          id="email"
          label="이메일"
          name="email"
          type="email"
          autoComplete="email"
          autoFocus
          value={email}
          onChange={(e) => {
            const value = e.target.value;
            setEmail(value);
            setEmailError(value && !EMAIL_RE.test(value) ? '유효한 이메일 주소를 입력하세요.' : '');
          }}
          error={!!emailError}
          helperText={emailError || (checkingEmail ? '이메일 확인 중...' : '')}
          sx={authFieldSx}
        />

        <TextField
          fullWidth
          id="username"
          label="사용자명"
          name="username"
          autoComplete="username"
          value={username}
          onChange={(e) => {
            setUsername(e.target.value);
            setUsernameError(validateUsername(e.target.value));
          }}
          error={!!usernameError}
          helperText={usernameError || ''}
          sx={authFieldSx}
        />

        <TextField
          fullWidth
          id="phoneNumber"
          label="휴대폰 번호"
          name="phoneNumber"
          placeholder="01012345678"
          value={phoneNumber}
          onChange={(e) => {
            const value = e.target.value.replace(/[^0-9-]/g, '');
            setPhoneNumber(value);
            setPhoneNumberError(value ? validatePhoneNumber(value) : '');
          }}
          error={!!phoneNumberError}
          helperText={phoneNumberError || '하이픈(-) 없이 입력하거나 포함하여 입력하세요.'}
          sx={authFieldSx}
        />

        <TextField
          fullWidth
          name="password"
          label="비밀번호"
          type="password"
          id="password"
          autoComplete="new-password"
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
            if (confirmPassword) {
              setPasswordError(e.target.value !== confirmPassword ? '비밀번호가 일치하지 않습니다.' : '');
            } else if (passwordError) {
              setPasswordError('');
            }
          }}
          error={!!passwordError}
          helperText={passwordError || '6자 이상'}
          sx={authFieldSx}
        />

        <TextField
          fullWidth
          name="confirmPassword"
          label="비밀번호 재입력"
          type="password"
          id="confirmPassword"
          autoComplete="new-password"
          value={confirmPassword}
          onChange={(e) => {
            setConfirmPassword(e.target.value);
            setPasswordError(e.target.value !== password ? '비밀번호가 일치하지 않습니다.' : '');
          }}
          error={!!passwordError}
          helperText={passwordError}
          sx={authFieldSx}
        />

        <Box sx={{ mb: 0.5, mt: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={agreeAll}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setAgree({ age: checked, terms: checked, privacy: checked });
                }}
                sx={checkboxSx}
              />
            }
            label={<Typography sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>전체동의</Typography>}
          />
        </Box>

        <ConsentRow
          checked={agree.age}
          onChange={(e) => setAgree((a) => ({ ...a, age: e.target.checked }))}
          label="만 14세 이상입니다"
        />
        <ConsentRow
          checked={agree.terms}
          onChange={(e) => setAgree((a) => ({ ...a, terms: e.target.checked }))}
          label="이용약관에 동의합니다"
          onView={openPopup('/terms', 'terms')}
        />
        <ConsentRow
          checked={agree.privacy}
          onChange={(e) => setAgree((a) => ({ ...a, privacy: e.target.checked }))}
          label="개인정보 수집 및 이용에 동의합니다"
          onView={openPopup('/privacy', 'privacy')}
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          color="primary"
          disabled={
            loading ||
            checkingEmail ||
            !!emailError ||
            !!usernameError ||
            !!phoneNumberError ||
            !!passwordError ||
            !agreeAll
          }
          sx={{ mt: 2, mb: 3, py: 1.5, borderRadius: '4px' }}
        >
          {loading ? '처리 중...' : '회원가입'}
        </Button>
      </Box>
    </AuthCard>
  );
};

export default Register;
